import { createSlice, PayloadAction, AnyAction } from "@reduxjs/toolkit";
import { ofType, Epic } from "redux-observable";
import { map, switchMap, catchError, mergeMap } from "rxjs/operators";
import { of, from, forkJoin, Observable } from "rxjs";
import { message } from "antd";
import i18n from "@/locales/i18n";
import { FileRecord } from "@/common/define";
import filesService from "@/features/file-management/pages/FilePage/services/files.service";
import { handleHttpError } from "@/api/HttpClient";
import { uploadFileActions } from "@/features/upload/stores/uploadFiles/uploadFileSlice";
import { getCompanyId } from "@/helper/company.helper";
import { isFormatViewable } from "@/config/file-security.config";
import { fileActions } from "./file";

interface ContextMenuState {
  isLoading: boolean;
  error: string | null;
}

interface OpenFilePayload {
  record: FileRecord;
  records?: FileRecord[];
  use3DViewer?: boolean;
}

interface MakeACopyPayload {
  records: any[];
}

const initialState: ContextMenuState = {
  isLoading: false,
  error: null,
};

const contextMenuSlice = createSlice({
  name: "contextMenu",
  initialState,
  reducers: {
    openFile: (state, action: PayloadAction<OpenFilePayload>) => {
      state.isLoading = true;
      state.error = null;
    },
    openFileSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    openFileError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    makeACopy: (state, _action: PayloadAction<MakeACopyPayload>) => {
      state.isLoading = true;
      state.error = null;
    },
    makeACopySuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    makeACopyError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

// Epic for handling file opening
export const openFileEpic: Epic<AnyAction, AnyAction, any, any> = (action$) =>
  action$.pipe(
    ofType(contextMenuActions.openFile.type),
    mergeMap((action: any) => {
      const { record, records, use3DViewer = false }: OpenFilePayload = action.payload;
      
      if (record.type === "folder") {
        const folderMessage = i18n.t("contextmenumessages:folder_navigate", { name: record.name });
        message.info(folderMessage);
        return of(contextMenuActions.openFileError(folderMessage));
      }
      
      const fileFormat = record?.format?.toLowerCase();

      if (fileFormat && isFormatViewable(fileFormat)) {
        if (!record?.cloudLink || !record?.bucketName || !record?.id) {
          const errorMessage = i18n.t("contextmenumessages:missing_file_info", { name: record.name });
          message.error(errorMessage);
          return of(contextMenuActions.openFileError(errorMessage));
        }

        const hideLoading = message.loading(i18n.t("contextmenumessages:opening_file", { name: record.name }), 0);

        return filesService.viewPDF(record.cloudLink, record.bucketName, record.id).pipe(
          map((res) => {
            hideLoading();
            if (res?.url) {
              window.open(res.url, "_blank");
              message.success(i18n.t("contextmenumessages:open_success", { name: record.name }));
              return contextMenuActions.openFileSuccess();
            } else {
              const userMessage = i18n.t("contextmenumessages:open_failed", { name: record.name });
              console.error(`URL not found for file:`, record);
              return contextMenuActions.openFileError(userMessage);
            }
          }),
          catchError((error) => {
            hideLoading();
            message.error(handleHttpError(error));
            const userMessage = i18n.t("contextmenumessages:open_failed", { name: record.name });
            console.error(`Error opening file "${record.name}":`, error);
            return of(contextMenuActions.openFileError(userMessage));
          })
        );
      } else {
        if (use3DViewer) {
          try {
            message.info(i18n.t("contextmenumessages:opening_3d_viewer", { name: record.name }));
            const fileList = (records || [record]).map((r) => ({
              baseFileId: r.cache?.baseFileId ?? r.baseFileId ?? r.id,
              baseMajorRev: 0,
              baseMinorRev: 0,
              fileName: r.fileName,
            }));
            const url = `https://3dviewer.inertia.hicas.vn/mainviewer?fileList=${encodeURIComponent(
              JSON.stringify(fileList)
            )}`;
            window.open(url, "_blank");
            return of(contextMenuActions.openFileSuccess());
          } catch (error) {
            const userMessage = i18n.t("contextmenumessages:open_3d_failed", { name: record.name });
            console.error(`3D viewer error for file "${record.name}":`, error);
            return of(contextMenuActions.openFileError(userMessage));
          }
        } else {
          const unsupportedMessage = fileFormat
            ? i18n.t("contextmenumessages:format_unsupported_with_format", { name: record.name, format: fileFormat })
            : i18n.t("contextmenumessages:format_unsupported", { name: record.name });

          message.info(unsupportedMessage);
          return of(contextMenuActions.openFileError(unsupportedMessage));
        }
      }
    })
  );

// Epic for handling make a copy
export const makeACopyEpic: Epic<AnyAction, AnyAction, any, any> = (action$) =>
  action$.pipe(
    ofType(contextMenuActions.makeACopy.type),
    mergeMap((action: any) => {
      const { records }: MakeACopyPayload = action.payload;

      if (!records?.length) {
        const errorMessage = i18n.t("contextmenumessages:no_files_selected");
        message.error(errorMessage);
        return of(contextMenuActions.makeACopyError(errorMessage));
      }

      // Separate folders and files
      const folders = records.filter(
        (r) => r.format === "FOLDER" || r.type === "folder" || r.isFolder
      );
      const files = records.filter(
        (r) => !(r.format === "FOLDER" || r.type === "folder" || r.isFolder)
      );

      const apiCalls: Observable<any>[] = [];
      const loadingMessages: string[] = [];
      const successMessages: string[] = [];

      // Handle folders
      if (folders.length > 0) {
        const labelIds = folders.map(f => f.id).filter(Boolean);
        
        if (labelIds.length > 0) {
          loadingMessages.push(
            folders.length > 1
              ? i18n.t("contextmenumessages:copying_multiple_folders", { count: folders.length })
              : i18n.t("contextmenumessages:copying_single_folder", { name: folders[0].name })
          );
          successMessages.push(
            folders.length > 1
              ? i18n.t("contextmenumessages:copy_multiple_folders_success", { count: folders.length })
              : i18n.t("contextmenumessages:copy_single_folder_success", { name: folders[0].name })
          );

          apiCalls.push(
            filesService.duplicateFolder(labelIds)
          );
        }
      }

      // Handle files
      if (files.length > 0) {
        const sourceDocumentIds = files.map(f => f.id).filter(Boolean);
        const destinationFolderId = files[0]?.parentId;

        if (!destinationFolderId || sourceDocumentIds.length === 0) {
          const errorMessage = i18n.t("contextmenumessages:missing_copy_info");
          message.error(errorMessage);
          return of(contextMenuActions.makeACopyError(errorMessage));
        }

        loadingMessages.push(
          files.length > 1
            ? i18n.t("contextmenumessages:copying_multiple_files", { count: files.length })
            : i18n.t("contextmenumessages:copying_single_file", { name: files[0].name })
        );
        successMessages.push(
          files.length > 1
            ? i18n.t("contextmenumessages:copy_multiple_files_success", { count: files.length })
            : i18n.t("contextmenumessages:copy_single_file_success", { name: files[0].name })
        );

        apiCalls.push(
          filesService.duplicateDocument(
            sourceDocumentIds,
            destinationFolderId,
            getCompanyId()
          )
        );
      }

      if (apiCalls.length === 0) {
        const errorMessage = i18n.t("contextmenumessages:no_valid_items");
        message.error(errorMessage);
        return of(contextMenuActions.makeACopyError(errorMessage));
      }

      const hideLoading = message.loading(
        loadingMessages.join(' '),
        0
      );

      // Execute all API calls in parallel
      return forkJoin(apiCalls).pipe(
        mergeMap(() => {
          hideLoading();

          message.success(successMessages.join(' '));

          const totalFileSize = files.reduce((sum, file) => {
            return sum + (file.sizeBytes || 0);
          }, 0);

          const actions: any[] = [
            contextMenuActions.makeACopySuccess(),
            uploadFileActions.setLastUploadAt(Date.now()),
            uploadFileActions.updateCompanyStorageSize(totalFileSize),
            fileActions.updateUserStorageSize(totalFileSize),
          ];

          return from(actions);
        }),
        catchError((error) => {
          hideLoading();
          const errorMsg = handleHttpError(error);
          message.error(errorMsg);
          return of(contextMenuActions.makeACopyError(errorMsg));
        })
      );
    })
  );

export const contextMenuActions = contextMenuSlice.actions;
export const contextMenuEpics = [openFileEpic, makeACopyEpic];

export default contextMenuSlice.reducer;
